import { v4 as uuidv4 } from "uuid";
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AddIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Box,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Card,
	Flex,
	Grid,
	Select,
	Switch,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { useEarmold, useEarmoldVariants } from "../hooks/earmolds.hooks";
import { useParams } from "react-router-dom";
import { useVariant } from "../hooks/variants.hooks";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import DotsGridIcon from "../components/icons/DotsGridIcon";
import TrashIcon from "../components/icons/TrashIcon";
import PrimaryButton from "../components/PrimaryButton";
import { useList } from "@uidotdev/usehooks";
import {
	type VariantCondition,
	VariantConditionInnerOperator,
	type VariantOption,
} from "../types/earmold.types";
import VariantForm, {
	type VariantFormHandler,
} from "../components/forms/VariantForm";
import _ from "lodash";
import { updateVariant } from "../mutations/variant.mutations";
import { useMutation, useQueryClient } from "react-query";
import InputTags from "../components/inputs/InputTags";
import Spacer from "../components/Spacer";
import { useSimpleToast } from "../hooks/toast.hooks";
import { createFile } from "../mutations/file.mutations";
import { Pen, Plus, Trash } from "lucide-react";
import SimpleModal from "../components/modals/SimpleModal";
import VariantOptionForm, {
	type VariantOptionFormHandler,
} from "../components/forms/VariantOptionForm";
import { VariantType } from "../types/variant.types";

type SortableVStackProps = {
	children: ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	items: any[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setItems: (items: any[]) => void;
};

export function SortableItem({ id }: { id: string }) {
	const { attributes, listeners, setNodeRef } = useSortable({ id });

	return (
		<div className="list-row" ref={setNodeRef}>
			<span>{id}</span>
			<button {...attributes} {...listeners}>
				⣿
			</button>
		</div>
	);
}

const SortableVStack = ({ children, items, setItems }: SortableVStackProps) => {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over !== null && active.id !== over?.id) {
			const oldIndex = items.findIndex((item) => item.uuid === active.id);
			const newIndex = items.findIndex((item) => item.uuid === over?.id);
			setItems(arrayMove(items, oldIndex, newIndex));
		}
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext items={items} strategy={verticalListSortingStrategy}>
				{children}
			</SortableContext>
		</DndContext>
	);
};

export function ProductVariantsDetailPageSortableItem({
	id,
	item,
	error,
	setItem,
	removeItem,
}: {
	id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	item: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setItem: (item: any) => void;
	removeItem: () => void;
}) {
	const { attributes, listeners, setNodeRef } = useSortable({
		id,
		transition: {
			duration: 150,
			easing: "cubic-bezier(0.25, 1, 0.5, 1)",
		},
	});

	return (
		<div ref={setNodeRef}>
			{/* Line */}
			<Grid
				alignItems="center"
				gap={4}
				pt={1}
				templateColumns="20px 1fr 20px 20px"
			>
				<DotsGridIcon {...attributes} {...listeners} color="#D0D5DD" />
				<Box className="grid grid-rows-2 bg-gray-50 border! border-gray-300 px-2 rounded-sm">
					<Text fontSize="16px" fontWeight={500}>
						{item.label}
					</Text>
					<Text fontSize="12px" fontWeight={400}>
						{item.description}
					</Text>
				</Box>
				<Pen
					size="16px"
					className="text-brand-700"
					onClick={() => setItem(item)}
				/>
				<Trash size="16px" className="text-red-700" onClick={removeItem} />
			</Grid>

			{/* Errors */}
			<Grid
				alignItems="center"
				gap={4}
				pt={1}
				templateColumns="20px 2fr 20rem 20rem 20px"
			>
				<Box></Box>
				<Box>
					{error?.label && (
						<Text
							mt="4px"
							pb={3}
							color="rgb(229, 62, 62)"
							lineHeight="17px"
							fontSize="14px"
							fontWeight="400"
						>
							{error.label}
						</Text>
					)}
				</Box>
				<Box></Box>
				<Box></Box>
				<Box></Box>
			</Grid>
		</div>
	);
}

const ProductVariantsDetailPage = () => {
	const variantFormRef = useRef<VariantFormHandler>(null);
	const variantOptionFormRef = useRef<VariantOptionFormHandler>(null);
	const { id, sid } = useParams();
	const [showImageInput, setShowImageInput] = useState<boolean>(false);
	const [showImageInputInitialized, setShowImageInputInitialized] =
		useState<boolean>(false);
	const [selectedType, setSelectedType] = useState<VariantType | undefined>(
		undefined,
	);
	const { data: earmold } = useEarmold(id);
	const { data: earmoldVariants } = useEarmoldVariants(id);
	const { data: variant } = useVariant(sid);
	const optionModal = useDisclosure();
	const [optionSelected, setOptionSelected] = useState<
		VariantOption | undefined
	>(undefined);
	const [
		options,
		{ set: setOptions, push: pushOption, removeAt: removeAtOptions },
	] = useList<VariantOption>([]);
	const [conditions, { push: pushCondition, set: setConditions }] =
		useList<VariantCondition>([]);
	useEffect(() => {
		if (variant != null && options.length === 0) {
			setOptions(variant?.options?.data ?? []);
			setConditions(variant?.options?.conditions ?? []);
		}
	}, [variant, options.length, setOptions, setConditions]);
	useEffect(() => {
		if (variant?.type) {
			setSelectedType(variant.type);
		}
	}, [variant?.type]);
	useEffect(() => {
		if (variant && !showImageInputInitialized) {
			setShowImageInput(Boolean(variant.fileId));
			setShowImageInputInitialized(true);
		}
	}, [variant, showImageInputInitialized]);
	const [editVariantDetail, setEditVariantDetail] = useState<boolean>(false);
	const [optionsError, { set: setOptionsError }] = useList<{
		label?: string;
	} | null>([]);
	const toast = useSimpleToast();
	const queryClient = useQueryClient();
	const { mutate: mutateUpdateVariant } = useMutation(updateVariant, {
		onSuccess: () => {
			setEditVariantDetail(false);
			queryClient.invalidateQueries("variant");
			toast.success("Sauvegarde", `Les modifications ont été enregistrées.`);
		},
		onError: () => {
			toast.error(
				"Sauvegarde",
				`Les modifications n'ont pas pu être sauvegardées. Essayer plus tard.`,
			);
		},
	});
	const allOptions = useMemo(
		() => earmoldVariants?.flatMap((variant) => variant.options.data) ?? [],
		[earmoldVariants],
	);
	const handleSave = async () => {
		// 0. Validate input
		const variantValid = variantFormRef.current
			? await variantFormRef.current.valid()
			: false;
		const tOptionsErrors = options.map((option) => {
			const optionError = {
				...((option.label === null || option.label === "") && {
					label: "Ce champ est requis",
				}),
			};
			return Object.keys(optionError).length === 0 ? null : optionError;
		});
		setOptionsError(tOptionsErrors);
		const optionsValid = _.every(
			tOptionsErrors.map((optionError) => optionError === null),
		);
		// 2. Save variant
		let payload = {};
		if (variantValid && variantFormRef.current) {
			// 2.1. Save file
			const formFile = variantFormRef.current.values().file;
			let file = null;
			if (formFile instanceof File) {
				file = await createFile({
					file: formFile,
					fileName: undefined,
				});
			}

			const nextFileId = (() => {
				if (showImageInput === false) {
					return null;
				}
				// `undefined` means unchanged, `null` means explicitly removed from FileImageInput.
				if (formFile === undefined) {
					return variant?.fileId ?? null;
				}
				if (formFile === null) {
					return null;
				}
				return file ? file.id : (variant?.fileId ?? null);
			})();
			payload = {
				...payload,
				label: variantFormRef.current.values().label,
				description: variantFormRef.current.values().description,
				type: variantFormRef.current.values().type,
				fileId: nextFileId,
			};
		}
		if (optionsValid && options) {
			payload = {
				...payload,
				options: {
					data: options.map((option) => ({
						cost: option.cost ?? 0,
						label: option.label ?? "",
						sageId: option.sageId ?? "",
						uuid: option.uuid,
						description: option.description ?? "",
						fileId: option.fileId ?? null,
					})),
					conditions,
				},
			};
		}
		mutateUpdateVariant({ id: sid, variant: payload });
	};
	const getPreviousVariants = ({ id }: { id: number }) => {
		if (earmoldVariants) {
			// 1. Find index
			const currentVariantIndex = earmoldVariants.findIndex((v) => v.id === id);
			// 2. If not on list return an empty list of elements
			if (currentVariantIndex === -1) {
				return [];
			}
			// 3. Otherwise map data to the expected shape
			return earmoldVariants.slice(0, currentVariantIndex).map((v) => ({
				label: v.label,
				value: v.id,
			}));
		}
		return [];
	};
	const getVariantOptions = ({ id }: { id: number | null }) => {
		if (id != null && earmoldVariants != null) {
			const variantIndex = earmoldVariants.findIndex((v) => v.id === id);
			if (variantIndex !== -1) {
				return earmoldVariants[variantIndex]?.options?.data.map((o) => ({
					label: o.label,
					value: o.uuid,
					uuid: o.uuid,
				}));
			}
		}
		return [];
	};
	const translateUuidToLabel = ({
		uuid,
		options,
	}: {
		uuid: string;
		options: { label: string; uuid: string }[];
	}) => {
		const option = options.find((option) => option.uuid === uuid);
		if (option) {
			return option.label;
		}
		return uuid;
	};
	return (
		<>
			<div className="flex flex-row justify-between">
				<Breadcrumb
					spacing="8px"
					separator={<ChevronRightIcon color="gray.500" />}
				>
					<BreadcrumbItem>
						<BreadcrumbLink
							href="/products"
							color="gray.600"
							fontWeight={600}
							fontSize="14px"
						>
							Produits
						</BreadcrumbLink>
					</BreadcrumbItem>

					<BreadcrumbItem>
						<BreadcrumbLink
							href={`/products/earmolds/${id}`}
							color="gray.600"
							fontWeight={600}
							fontSize="14px"
						>
							{earmold && earmold.label}
						</BreadcrumbLink>
					</BreadcrumbItem>

					<BreadcrumbItem>
						<BreadcrumbLink
							href="#"
							color="brand.700"
							fontWeight={600}
							fontSize="14px"
						>
							{variant && variant.label}
						</BreadcrumbLink>
					</BreadcrumbItem>
				</Breadcrumb>
			</div>

			<SimpleModal
				title={optionSelected ? "Modifier la valeur" : "Ajouter une valeur"}
				isOpen={optionModal.isOpen}
				onClose={optionModal.onClose}
				acceptText={optionSelected ? "Modifier la valeur" : "Ajouter la valeur"}
				icon={<Plus size="22px" strokeWidth={2} className="text-brand-700" />}
				onClick={async () => {
					const values = variantOptionFormRef.current?.values();
					if (values) {
						let fileId = null;
						if (selectedType === VariantType.IMAGE_PER_ITEM && values.file) {
							const file = await createFile({
								file: values.file as File,
								fileName: undefined,
							});
							if (file?.id) {
								fileId = file.id;
							}
						}
						if (optionSelected === undefined) {
							pushOption({
								uuid: uuidv4(),
								label: values.label ?? "",
								cost: values.cost ? values.cost : 0,
								sageId: values.sageId ?? "",
								description: values.description ?? "",
								fileId,
							});
						} else {
							setOptions(
								options.map((option) => {
									if (option.uuid !== optionSelected.uuid) {
										return option;
									}
									return {
										...option,
										label: values.label ?? "",
										cost: values.cost ? values.cost : 0,
										sageId: values.sageId ?? "",
										description: values.description ?? "",
										fileId,
									};
								}),
							);
						}
					}
					optionModal.onClose();
				}}
			>
				<VariantOptionForm
					ref={variantOptionFormRef}
					value={optionSelected}
					asImageInput={selectedType === VariantType.IMAGE_PER_ITEM}
				/>
			</SimpleModal>

			{/* VARIANT DETAILS */}
			{editVariantDetail === true && (
				<Card mt={4} p={4}>
					<div className="flex flex-row justify-between">
						<Text lineHeight="27px" fontSize="18px" fontWeight="600">
							{variant && variant.label}
						</Text>

						<div className="flex flex-row gap-2 items-center">
							<Switch
								size="sm"
								colorScheme="brand"
								onChange={() => setShowImageInput((prev) => !prev)}
								isChecked={showImageInput}
								cursor="pointer"
							/>
							<Text
								fontSize="14px"
								fontWeight={500}
								cursor="pointer"
								onClick={() => setShowImageInput((prev) => !prev)}
							>
								Ajouter un visuel
							</Text>
						</div>
					</div>

					<VariantForm
						ref={variantFormRef}
						value={variant ?? undefined}
						showImageInput={showImageInput}
						onTypeChange={(type) => setSelectedType(type)}
					></VariantForm>

					<Text
						lineHeight="18px"
						fontSize="12px"
						fontWeight="500"
						color="gray.500"
						mb={4}
					>
						Valeurs de l'option
					</Text>

					{options && (
						<SortableVStack items={options} setItems={setOptions}>
							{options.map((option, index) => (
								<ProductVariantsDetailPageSortableItem
									key={option.uuid}
									id={option.uuid}
									item={option}
									setItem={(item: VariantOption) => {
										setOptionSelected(item);
										optionModal.onOpen();
									}}
									removeItem={() => removeAtOptions(index)}
									error={
										optionsError && index < optionsError.length
											? optionsError[index]
											: {}
									}
								/>
							))}
						</SortableVStack>
					)}

					<Grid
						onClick={() => {
							setOptionSelected(undefined);
							optionModal.onOpen();
						}}
						paddingY={4}
						gridTemplateColumns="36px 1fr"
					>
						<Box></Box>
						<Box className="flex flex-row items-center gap-2 text-brand-700 hover:underline cursor-pointer!">
							<Plus size="13px" strokeWidth={3} />
							<Text fontSize="14px" fontWeight={600}>
								Ajouter une valeur
							</Text>
						</Box>
					</Grid>

					<PrimaryButton w="fit-content" mt={4} onClick={handleSave}>
						Sauvegarder
					</PrimaryButton>
				</Card>
			)}
			{editVariantDetail === false && (
				<Card mt={4} p={4}>
					<Flex justifyContent="space-between">
						<Text lineHeight="27px" fontSize="18px" fontWeight="600">
							{variant && variant.label}
						</Text>
						<Button w="fit-content" onClick={() => setEditVariantDetail(true)}>
							Modifier
						</Button>
					</Flex>

					<Flex gap={4} mt={4}>
						{variant?.options?.data?.map((data) => (
							<Box
								key={data.uuid}
								w="fit-content"
								border="solid 1px"
								borderColor="gray.300"
								borderRadius="8px"
								px={2}
								fontSize="12px"
								fontWeight={500}
								lineHeight="18px"
							>
								{data.label}
							</Box>
						))}
					</Flex>
				</Card>
			)}

			{/* CONDITIONS */}
			<Card mt={4} p={4}>
				<Flex direction="column">
					<Text lineHeight="27px" fontSize="18px" fontWeight="600" mb={4}>
						Logiques conditionnelles
					</Text>

					{conditions &&
						conditions.map((condition, index) => (
							<>
								{index !== 0 && (
									<Box position="relative" my={4}>
										<hr></hr>
										<Text
											color="gray.400"
											position="absolute"
											top="-13px"
											left="10px"
											backgroundColor="white"
											px="10px"
											zIndex={3}
										>
											ou
										</Text>
									</Box>
								)}
								<Box>
									{/* VARIANT OPTION SELECTION */}
									<Grid templateColumns={"4em 1fr"} alignItems="center">
										<Text>Pour</Text>
										<InputTags
											my={3}
											options={options.map((option) => ({
												label: option.label,
												value: option.uuid,
											}))}
											selected={condition.targets.map((t) => ({
												label: translateUuidToLabel({
													uuid: t,
													options: allOptions,
												}),
												value: t,
											}))}
											setSelected={(selected) => {
												const conditionsCopy = [...conditions];
												if (index < conditionsCopy.length) {
													conditionsCopy[index].targets = selected.map(
														(s) => s.value,
													);
													setConditions(conditionsCopy);
												}
											}}
										></InputTags>
									</Grid>
									{/* CONDITIONS SELECTION */}
									{condition.conditions &&
										condition.conditions.map((subcondition, subindex) => (
											<Grid
												key={subcondition.variantId}
												templateColumns={"4em 1fr 1fr 2fr 40px"}
												alignItems="center"
											>
												<Text>{subindex === 0 ? "Si" : "et si"}</Text>
												<Box mr={4}>
													<Select
														value={subcondition.variantId ?? undefined}
														onChange={(evt) => {
															const conditionsCopy = [...conditions];
															if (
																index < conditionsCopy.length &&
																subindex <
																	conditionsCopy[index].conditions.length
															) {
																const value = Number.parseInt(evt.target.value);
																conditionsCopy[index].conditions[
																	subindex
																].variantId = Number.isNaN(value)
																	? null
																	: value;
																setConditions(conditionsCopy);
															}
														}}
													>
														<option value={undefined}>---</option>
														{variant &&
															getPreviousVariants({
																id: variant.id,
															}).map((pv) => (
																<option key={pv.value} value={pv.value}>
																	{pv.label}
																</option>
															))}
													</Select>
												</Box>
												<Box mr={4}>
													<Select
														value={subcondition.operator}
														onChange={(evt) => {
															const conditionsCopy = [...conditions];
															if (
																index < conditionsCopy.length &&
																subindex <
																	conditionsCopy[index].conditions.length
															) {
																conditionsCopy[index].conditions[
																	subindex
																].operator = evt.target
																	.value as VariantConditionInnerOperator;
																setConditions(conditionsCopy);
															}
														}}
													>
														<option value="==">Est égal</option>
														<option value="!=">N'est pas égal</option>
													</Select>
												</Box>
												<InputTags
													my={3}
													options={getVariantOptions({
														id: subcondition.variantId,
													})}
													selected={subcondition.values.map((v) => ({
														label: translateUuidToLabel({
															uuid: v,
															options: getVariantOptions({
																id: subcondition.variantId,
															}),
														}),
														value: v,
													}))}
													setSelected={(selected) => {
														const conditionsCopy = [...conditions];
														if (
															index < conditionsCopy.length &&
															subindex < conditionsCopy[index].conditions.length
														) {
															conditionsCopy[index].conditions[
																subindex
															].values = selected.map((s) => s.value);
															setConditions(conditionsCopy);
														}
													}}
												></InputTags>
												<Flex justifyContent="end" alignItems="center">
													<TrashIcon
														onClick={() => {
															const conditionsCopy = [...conditions];
															if (
																index < conditionsCopy.length &&
																subindex <
																	conditionsCopy[index].conditions.length
															) {
																conditionsCopy[index].conditions.splice(
																	subindex,
																	1,
																);
																setConditions(conditionsCopy);
															}
														}}
													></TrashIcon>
												</Flex>
											</Grid>
										))}
									{/* ADD CONDITION LINK */}
									<Grid templateColumns={"4em 1fr"} alignItems="center" my={4}>
										<Box
											onClick={() => {
												const conditionsCopy = [...conditions];
												if (index < conditionsCopy.length) {
													conditionsCopy.splice(index, 1);
													setConditions(conditionsCopy);
												}
											}}
										>
											<TrashIcon></TrashIcon>
										</Box>
										<Button
											variant="link"
											w="fit-content"
											color="brand.700"
											onClick={() => {
												const conditionsCopy = [...conditions];
												if (index < conditionsCopy.length) {
													conditionsCopy[index].conditions.push({
														variantId: null,
														operator: VariantConditionInnerOperator.EQUAL,
														values: [],
													});
													setConditions(conditionsCopy);
												}
											}}
										>
											<AddIcon mr={4}></AddIcon>Ajouter une condition
										</Button>
									</Grid>
								</Box>
							</>
						))}

					<Spacer my={4}></Spacer>

					<Flex justify="space-between">
						<Button
							variant="link"
							w="fit-content"
							color="brand.700"
							onClick={() =>
								pushCondition({
									targets: [],
									conditions: [
										{
											variantId: null,
											values: [],
											operator: VariantConditionInnerOperator.EQUAL,
										},
									],
								})
							}
						>
							<AddIcon mr={4}></AddIcon>Ajouter des logiques
						</Button>

						<Button
							variant="solid"
							bgColor="brand.700"
							color="white"
							onClick={handleSave}
						>
							Sauvegarder
						</Button>
					</Flex>
				</Flex>
			</Card>
		</>
	);
};

export default ProductVariantsDetailPage;
